import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tag, Send, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `€${Number(v || 0).toFixed(2).replace(".", ",")}`;

const RecoveryModal = ({ cart, initialDiscount, onClose, onSent }) => {
  const [withDiscount, setWithDiscount] = useState(initialDiscount);
  const [pct, setPct] = useState(10);
  const [customPct, setCustomPct] = useState("");
  const [hours, setHours] = useState(48);
  const [customHours, setCustomHours] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const effPct = pct === "custom" ? Number(customPct) : pct;
  const effHours = hours === "custom" ? Number(customHours) : hours;
  const rangeOk = !withDiscount || (effPct >= 1 && effPct <= 30);
  const hoursOk = effHours >= 1;

  const loadPreview = useCallback(async () => {
    if (withDiscount && !rangeOk) { setPreview(null); return; }
    try {
      const { data } = await api.get(`/admin/carts/${cart.id}/recovery/preview`, {
        params: { with_discount: withDiscount, discount_percent: withDiscount ? effPct : undefined, expiry_hours: effHours || 48 } });
      setPreview(data);
      setMessage((prev) => prev || data.default_message || "");
    } catch (err) { setPreview(null); toast.error(formatApiError(err.response?.data?.detail)); }
  }, [cart.id, withDiscount, effPct, effHours, rangeOk]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  // aggiorna il testo di default quando cambio tipo (solo se non modificato manualmente)
  useEffect(() => { setMessage(""); }, [withDiscount]);

  const offer = preview?.offer;
  const consentBlocked = withDiscount && preview && preview.consent_ok === false;

  const send = async () => {
    if (withDiscount && !rangeOk) { toast.error("Sconto tra 1% e 30%"); return; }
    if (!hoursOk) { toast.error("Scadenza non valida"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/carts/${cart.id}/recovery`, {
        with_discount: withDiscount, discount_percent: withDiscount ? effPct : undefined,
        expiry_hours: effHours || 48, custom_message: message });
      toast.success(`Recupero inviato (email: ${data.email_status})`);
      onSent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="recovery-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-brand-electric" /> Anteprima recupero</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2" data-testid="recovery-type-toggle">
            <button data-testid="rt-plain" onClick={() => setWithDiscount(false)} className={`px-3 py-1.5 rounded-full text-sm ${!withDiscount ? "bg-brand-navy text-white" : "bg-white border border-brand-navy/15 text-brand-navy"}`}>Senza sconto</button>
            <button data-testid="rt-discount" onClick={() => setWithDiscount(true)} className={`px-3 py-1.5 rounded-full text-sm ${withDiscount ? "bg-brand-navy text-white" : "bg-white border border-brand-navy/15 text-brand-navy"}`}>Con sconto</button>
          </div>

          {withDiscount && (
            <>
              <div>
                <p className="text-sm font-medium text-brand-navy mb-2">Sconto</p>
                <div className="flex flex-wrap gap-2">
                  {[5, 10, 15, "custom"].map((v) => (
                    <button key={v} data-testid={`disc-${v}`} onClick={() => setPct(v)} className={`px-3 py-1.5 rounded-full text-sm ${pct === v ? "bg-brand-electric text-white" : "bg-white border border-brand-navy/15 text-brand-navy"}`}>{v === "custom" ? "Personalizzato" : `${v}%`}</button>
                  ))}
                </div>
                {pct === "custom" && <Input data-testid="disc-custom-input" type="number" min={1} max={30} value={customPct} onChange={(e) => setCustomPct(e.target.value)} placeholder="1–30" className="mt-2 w-32" />}
              </div>
              <div>
                <p className="text-sm font-medium text-brand-navy mb-2">Validità</p>
                <div className="flex flex-wrap gap-2">
                  {[24, 48, 72, "custom"].map((v) => (
                    <button key={v} onClick={() => setHours(v)} className={`px-3 py-1.5 rounded-full text-sm ${hours === v ? "bg-brand-electric text-white" : "bg-white border border-brand-navy/15 text-brand-navy"}`}>{v === "custom" ? "Personalizzata" : `${v} ore`}</button>
                  ))}
                </div>
                {hours === "custom" && <Input type="number" min={1} value={customHours} onChange={(e) => setCustomHours(e.target.value)} placeholder="ore" className="mt-2 w-32" />}
              </div>
            </>
          )}

          <div>
            <p className="text-sm font-medium text-brand-navy mb-1">Messaggio (modificabile)</p>
            <Textarea data-testid="recovery-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Testo introduttivo dell'email…" />
            <p className="mt-1 text-xs text-brand-navy/50">Puoi modificare solo il testo introduttivo. Prodotti, totali, sconto, scadenza e link di recupero sono generati automaticamente e non modificabili.</p>
          </div>

          {preview && (
            <div className="rounded-xl bg-brand-light p-3 text-sm space-y-1" data-testid="recovery-preview-summary">
              <div className="flex justify-between"><span>Cliente</span><span className="font-medium">{preview.user_email}</span></div>
              <div className="mt-1">
                {(preview.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-brand-navy/70"><span>{it.title} ×{it.quantity}</span><span>{money(it.price * it.quantity)}</span></div>
                ))}
              </div>
              <div className="flex justify-between border-t border-brand-navy/10 pt-1"><span>Totale originale</span><span>{money(preview.original_total)}</span></div>
              {offer && <div className="flex justify-between text-brand-electric"><span>Sconto {offer.percent}%</span><span>-{money(offer.amount)}</span></div>}
              {offer && <div className="flex justify-between"><span>Risparmio</span><span>{money(offer.amount)}</span></div>}
              {offer && <div className="flex justify-between font-bold"><span>Totale con sconto</span><span data-testid="preview-final-total">{money(offer.discounted_total)}</span></div>}
              {offer && <div className="flex justify-between text-brand-navy/60"><span>Scadenza</span><span>{offer.expires_at_human}</span></div>}
              <div className="text-xs text-brand-navy/50 pt-1">CTA: {withDiscount ? "Completa il tuo acquisto" : "Riprendi il tuo carrello"} · {preview.recovery_url_note}</div>
            </div>
          )}

          {consentBlocked && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm p-3" data-testid="consent-blocked">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {preview.consent_reason} Non è possibile inviare un recupero con sconto.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Annulla</Button>
          <Button data-testid="confirm-send" onClick={send} disabled={sending || consentBlocked || (withDiscount && !rangeOk) || !hoursOk}
            className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">
            {sending ? "Invio…" : (withDiscount ? "Invia recupero con sconto" : "Invia recupero")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminAbandonedCarts = () => {
  const [carts, setCarts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { cart, initialDiscount }

  const load = async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([api.get("/admin/carts/abandoned"), api.get("/admin/carts/metrics")]);
      setCarts(a.data.carts); setMaxAttempts(a.data.max_attempts); setMetrics(m.data);
    } catch { setCarts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-abandoned-carts">
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[["Abbandonati", metrics.abandoned], ["Recuperi inviati", metrics.recovery_sent],
            ["Recuperati", metrics.recovered], ["Valore recuperato", money(metrics.recovered_value)],
            ["Tasso recupero", `${metrics.recovery_rate}%`]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-brand-navy/10 bg-white p-4">
              <p className="text-xs text-brand-navy/50">{l}</p>
              <p className="text-xl font-bold text-brand-navy mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-brand-navy">Carrelli abbandonati</h3>
        <Button variant="outline" size="sm" onClick={load} className="rounded-full gap-2"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
      </div>
      {loading ? <p className="text-brand-navy/50 py-8">Caricamento…</p> : carts.length === 0 ? (
        <p data-testid="carts-empty" className="text-brand-navy/50 py-8">Nessun carrello abbandonato.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-brand-navy/10">
          <table className="w-full text-sm">
            <thead className="bg-brand-light text-brand-navy/60 text-left">
              <tr>{["Cliente", "Totale", "Età", "Pag.", "Recuperi", "Stato recovery", "Azioni"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {carts.map((c) => {
                const maxed = (c.recovery_count || 0) >= maxAttempts;
                return (
                  <tr key={c.id} data-testid={`cart-row-${c.id}`} className="border-t border-brand-navy/5">
                    <td className="px-3 py-2">{c.user_email}</td>
                    <td className="px-3 py-2">{money(c.total)}</td>
                    <td className="px-3 py-2">{c.age_minutes}m</td>
                    <td className="px-3 py-2">{c.payment_status}</td>
                    <td className="px-3 py-2">{c.recovery_count || 0}/{maxAttempts}</td>
                    <td className="px-3 py-2">{c.recovery_status}{c.recovery_offer ? ` (${c.recovery_offer.percent}%)` : ""}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        {maxed ? <span className="text-xs text-brand-navy/40">Numero massimo di recuperi raggiunto.</span> : (
                          <>
                            <Button data-testid={`send-plain-${c.id}`} size="sm" variant="outline" onClick={() => setModal({ cart: c, initialDiscount: false })} className="rounded-full gap-1 text-xs"><Send className="h-3 w-3" /> Invia recupero</Button>
                            <Button data-testid={`send-discount-${c.id}`} size="sm" onClick={() => setModal({ cart: c, initialDiscount: true })} className="rounded-full gap-1 text-xs bg-brand-electric hover:bg-brand-electric/90 text-white"><Tag className="h-3 w-3" /> Con sconto</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {modal && <RecoveryModal cart={modal.cart} initialDiscount={modal.initialDiscount} onClose={() => setModal(null)} onSent={() => { setModal(null); load(); }} />}
    </div>
  );
};

export default AdminAbandonedCarts;
